import React, { useEffect, useState } from "react";
import config from "../../config";

// The laser's signal tower, mirrored in the kiosk header: a bar that pulses the colour
// of the light that is lit. GET /laser/signal-light decides the colour (red wins while a
// fault is open); the kiosk only draws it.

const POLL_MS = 3000;
// After this many failed reads in a row the bar says it has lost the light, rather than
// keep pulsing a colour that may be minutes old.
const MAX_MISSES = 3;

export const SIGNAL_LOOKS = {
  red: { color: "#ff3b3b", text: "LASER FAULT", period: "0.8s" },
  yellow: { color: "#ffc107", text: "STANDBY", period: "1.6s" },
  green: { color: "#2fd158", text: "CUTTING", period: "2.4s" },
};

export function faultAge(redSince, now = Date.now()) {
  if (!redSince) return null;
  const mins = Math.floor((now - new Date(redSince).getTime()) / 60000);
  if (!(mins >= 1)) return null;
  return mins < 60 ? `${mins} min` : `${Math.floor(mins / 60)} h ${mins % 60} min`;
}

const KEYFRAMES = `
@keyframes kioskSignalPulse {
  0%, 100% { opacity: 1; box-shadow: 0 0 18px 4px var(--signal-glow); }
  50% { opacity: 0.35; box-shadow: 0 0 2px 0 var(--signal-glow); }
}
@media (prefers-reduced-motion: reduce) {
  .kiosk-signal-bar { animation: none !important; }
}`;

export default function SignalLightBar() {
  const [signal, setSignal] = useState(null);
  const [misses, setMisses] = useState(0);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let alive = true;
    const read = async () => {
      try {
        const res = await fetch(`${config.API_BASE_URL}/laser/signal-light`, { cache: "no-store" });
        // An API without the route (404) means nothing to mirror: draw nothing.
        if (res.status === 404) { if (alive) setUnavailable(true); return; }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.json();
        if (!alive) return;
        setUnavailable(false);
        setSignal(body);
        setMisses(0);
      } catch (err) {
        if (alive) setMisses((m) => m + 1);
      }
    };
    read();
    const id = setInterval(read, POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (unavailable) return null;

  const lost = misses >= MAX_MISSES;
  const look = !lost && signal ? SIGNAL_LOOKS[signal.color] : null;
  const age = look && signal.color === "red" ? faultAge(signal.red_since) : null;

  return (
    <div
      data-testid="signal-light-bar"
      data-signal={look ? signal.color : "none"}
      style={{ flex: 1, minWidth: 120, display: "flex", alignItems: "center", alignSelf: "stretch" }}
    >
      <style>{KEYFRAMES}</style>
      <div
        className="kiosk-signal-bar"
        title={look ? `Laser signal tower: ${signal.color}` : "No reading from the laser signal tower"}
        style={{
          "--signal-glow": look ? look.color : "transparent",
          flex: 1,
          height: "2.6rem",
          borderRadius: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.75rem",
          background: look ? look.color : "#2a2f38",
          border: look ? "none" : "2px dashed #4a5260",
          color: look ? (signal.color === "yellow" ? "#1a1d24" : "#0f1115") : "#8fa3b8",
          fontWeight: 800,
          fontSize: "1.35rem",
          letterSpacing: "0.12em",
          animation: look ? `kioskSignalPulse ${look.period} ease-in-out infinite` : "none",
        }}
      >
        {look ? look.text : lost ? "SIGNAL LIGHT: NO READING" : "SIGNAL LIGHT"}
        {age && <span style={{ fontSize: "1rem", letterSpacing: "0.04em" }}>{age}</span>}
      </div>
    </div>
  );
}
